import React, { useCallback, useEffect, useMemo, useState } from "react";
import Page from "../../../layout/Page";
import { useSelector } from "react-redux";
import { AppState } from "../../../../redux";
import RoleRepository from "../../../../common/repository/RoleRespository";
import StateDistrictRepository from "../../../../common/repository/StateDistrictRepository";
import UserRepository from "../../../../common/repository/UserRepository";
import StatesDistrictsRepository from "../../../../common/repository/StatesDistrictsRepository";
import { DropdownItemProps, Tab } from "semantic-ui-react";
import Result from "../../../../common/repository/Result";
import { toast } from "../../../common/Toast";
import constants from "../../../../common/constants";
import FoStatusContext from './FoStatusFilters/FoStatusContext'
import FoStatusFilters from "./FoStatusFilters/FoStatusFilters";
import FoCloseStatusTable from "./FoCloseTable/FoCloseStatusTable";
import FoOpenStatusTable from "./FoOpenTable/FoOpenStatusTable";


const FoCloseStatus = () => {
  const authState = useSelector((state: AppState) => state.auth);
  const loggedInUser = authState.user;

  const roleRepo = useMemo(() => new RoleRepository(), []);
  const stateRepo = useMemo(() => new StateDistrictRepository(), []);
  const userRepo = useMemo(() => new UserRepository(), []);
  const statesDistRepo = useMemo(() => new StatesDistrictsRepository(), []);

  const [stateOptions, setStateOptions] = useState<DropdownItemProps[]>([]);
  const [districtOptions, setDistrictOptions] = useState<DropdownItemProps[]>([]);
  const [AllDistrictS, setAllDistricts] = useState<DropdownItemProps[]>([]);
  const [roleOptions, setRoleOptions] = useState<DropdownItemProps[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownItemProps[]>([]);
  const [loadingEssentialData, setLoadingEssentialData] = useState(true);

  const [userRole, setUserRole] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [states, setStates] = useState("");
  const [district, setDistrict] = useState("");
  const [reportee, setReportee] = useState<any[]>([]);
  const [claimNumber, setClaimNumber] = useState("");

  const effectLoadEssentialData = useCallback(async () => {
    try {
      const [rolesResult, districtsResult, statesResult] =
        await Promise.all([
          roleRepo.getRoles({
            level_greater_than: loggedInUser?.getRole().getLevel(),
          }),
          stateRepo.getAll(),
          statesDistRepo.getStates(),
        ]);

      if (rolesResult instanceof Result.Success) {
        setRoleOptions(
          rolesResult.data.items.map((item) => {
            const reporteeData = item.getReportee();
            const formattedReportee = Array.isArray(reporteeData)
              ? Array.from(new Set(reporteeData.filter(Boolean)))
              : typeof reporteeData === "string"
                ? [reporteeData]
                : [];

            const title = String(item.getTitle());

            return {
              text: title,
              value: title,
              key: item.getId(),
              reportee: formattedReportee,
            };
          })
        );
      } else {
        toast.error(rolesResult.message || "Could not load User Roles");
      }

      if (statesResult instanceof Result.Success) {
        setStateOptions(
          statesResult.data.items.map((item) => ({
            text: item.getName(),
            value: item.getName(),
            key: item.getId(),
          }))
        );
      } else {
        toast.error(statesResult.message || "Could not load States");
      }

      if (districtsResult instanceof Result.Success) {
        const allDistrictOptions = districtsResult.data.items.map((item, index) => ({
          text: `${item.getDistrict()}, ${item.getState()}`,
          value: JSON.stringify({
            state: item.getState(),
            district: item.getDistrict(),
          }),
          key: index.toString(),
        }));

        setDistrictOptions(allDistrictOptions);
        setAllDistricts(allDistrictOptions);
      } else {
        toast.error(districtsResult.message || "Could not load Districts");
      }
    } catch {
      toast.error("Could not load filter data");
    } finally {
      setLoadingEssentialData(false);
    }
  }, [loggedInUser, roleRepo, stateRepo, statesDistRepo]);

  useEffect(() => {
    effectLoadEssentialData();
  }, [effectLoadEssentialData]);

  // Users: load by selected role (same as Home page)
  const effectLoadUsers = useCallback(async () => {
    if (userRole.length === 0) {
      setUserOptions([]);
      return;
    }
    const filter = { role: userRole, findTeam: false, is_active: true };
    if (
      userRole === constants.roleTitle.ca ||
      userRole === constants.roleTitle.tl ||
      userRole === constants.roleTitle.oh
    )
      filter.findTeam = true;

    const result = await userRepo.getUsers(filter);
    if (result instanceof Result.Success) {
      setUserOptions(
        result.data.items.map((item) => ({
          text: item.getName(),
          value: item.getId(),
          key: item.getId(),
        }))
      );
    } else {
      toast.error(result.message || "Could not load Users");
    }
  }, [userRole, userRepo]);

  useEffect(() => {
    effectLoadUsers();
  }, [effectLoadUsers]);

  // Districts: when a state is selected show only that state's districts,
  // otherwise show every "district, state" combination (same as Home page)
  const effectLoadDistricts = useCallback(async () => {
    if (states.length === 0) {
      setDistrictOptions(AllDistrictS);
      return;
    }
    const result = await statesDistRepo.getDistricts({ state: states });
    if (result instanceof Result.Success) {
      setDistrictOptions(
        result.data.items.map((item: string, i: number) => ({
          text: item,
          value: item,
          key: i.toString(),
        }))
      );
    } else {
      toast.error(result.message || "Could not load District");
    }
  }, [states, statesDistRepo, AllDistrictS]);

  useEffect(() => {
    effectLoadDistricts();
  }, [effectLoadDistricts]);

  const handleDistrictOptionsChange = (
    value: React.SetStateAction<DropdownItemProps[]>
  ) => {
    setDistrictOptions(value);
  };

  const MENU_ITEMS = [
    {
      menuItem: 'Closed Cases', render: () =>
        <>
          <FoStatusFilters />
          <FoCloseStatusTable />
        </>
    },
    {
      menuItem: 'Open Cases', render: () =>
        <>
          <FoStatusFilters />
          <FoOpenStatusTable />
        </>
    },
  ]

  return (
    <FoStatusContext.Provider
      value={{
        options: {
          states: stateOptions,
          districts: districtOptions,
          user: userOptions,
          userRoles: roleOptions,
        },
        filters: {
          userRole,
          setUserRole,
          user: selectedUser,
          setUser: setSelectedUser,
          states,
          setStates,
          district,
          setDistrict,
          reportee,
          setReportee,
          claimNumber,
          setClaimNumber,
          AllDistrictS,
          setDistrictOptions: handleDistrictOptionsChange,
        },
      }}
    >
      <Page title="Field Close/Open Cases" description="Manage Field Close/Open Status">
        <Tab
          menu={{ secondary: true, pointing: true }}
          panes={MENU_ITEMS} />
      </Page>
    </FoStatusContext.Provider>
  );
};

export default FoCloseStatus;